/**
 * Reglas de ocupación de recursos (Sprint 22).
 *
 * Espejo en frontend de los invariantes que el backend hace cumplir en
 * `services/recursos.py` + los índices UNIQUE parciales:
 *
 *   1 llavero vivo por PERSONA        (uq_recursos_llavero_activo_por_residente)
 *   1 control remoto vivo por AUTO    (uq_recursos_cr_activo_por_vehiculo)
 *
 * El backend es la autoridad y responde 409 si algo se cuela. Estos helpers
 * existen para que el vecino y el tesorero NUNCA lleguen a ese 409: el
 * candidato ocupado simplemente no aparece en el selector.
 *
 * Origen del cambio: la casa 6975 acumuló cuatro llaveros a nombre del
 * titular porque el portal ofrecía siempre al único residente registrado, y
 * cada fila del formulario ignoraba lo que habían elegido las otras.
 */
import type {
  EstadoRecurso,
  MotivoBaja,
  RecursoConSuspensionRead,
} from './api/types';

/**
 * Estados en que un recurso sigue en manos de alguien y por tanto OCUPA el
 * slot de esa persona (llavero) o de ese auto (control remoto).
 *
 * Espejo de `ESTADOS_RECURSO_VIVO` en `app/models/recurso.py`. `reportado`
 * cuenta como vivo: el vecino reportó el problema pero el recurso sigue
 * físicamente con él hasta que la Comisión confirma la baja. Los terminales
 * (`de_baja`, `perdido`, `devuelto`, `reemplazado`) liberan el slot — es lo
 * que permite entregar el reemplazo.
 */
export const ESTADOS_RECURSO_VIVO: readonly EstadoRecurso[] = [
  'entregado',
  'reportado',
];

export function esRecursoVivo(recurso: RecursoConSuspensionRead): boolean {
  return ESTADOS_RECURSO_VIVO.includes(recurso.estado);
}

/**
 * True si el recurso admite las operaciones de asignar / liberar.
 *
 * Solo los recursos vivos y de una casa. Se excluyen:
 * - estados terminales: el puntero al portador queda como registro histórico
 *   de quién lo tenía al darse de baja, y no se toca;
 * - recursos donados a un ente externo (cartero, citofonía, camión de basura):
 *   no tienen casa ni portador, y para cambiarlos hay que dar de baja el
 *   recurso y crear otro (D-P14-4).
 */
export function esRecursoAsignable(recurso: {
  estado: EstadoRecurso;
  lote_id: string | null;
  ente_externo_id?: string | null;
}): boolean {
  return (
    ESTADOS_RECURSO_VIVO.includes(recurso.estado) &&
    recurso.lote_id !== null &&
    !recurso.ente_externo_id
  );
}

/** Ids de residentes que ya portan un llavero vivo. No pueden recibir otro. */
export function residentesConLlaveroVivo(
  recursos: RecursoConSuspensionRead[],
): Set<string> {
  return new Set(
    recursos
      .filter(
        (r) =>
          r.tipo === 'llavero' &&
          esRecursoVivo(r) &&
          r.residente_actual_id !== null,
      )
      .map((r) => r.residente_actual_id as string),
  );
}

/** Ids de vehículos que ya tienen un control remoto vivo. */
export function vehiculosConCrVivo(
  recursos: RecursoConSuspensionRead[],
): Set<string> {
  return new Set(
    recursos
      .filter(
        (r) =>
          r.tipo === 'control_remoto' &&
          esRecursoVivo(r) &&
          r.vehiculo_id !== null,
      )
      .map((r) => r.vehiculo_id as string),
  );
}

/** Etiquetas de los motivos de baja (backend `MOTIVOS_BAJA`, 2026-10-06).
 *  Fuente única para el modal "Dar de baja" y la lista "Por desprogramar". */
export const MOTIVO_BAJA_LABEL: Record<MotivoBaja, string> = {
  perdido: 'Perdido',
  robado: 'Robado',
  llevado_ex_residente: 'Se lo llevó un ex-residente',
  danado: 'Dañado sin reposición',
  otro: 'Otro',
};
